import React, { useMemo, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useData } from './DataProvider';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * ScenarioSpans
 * Renders time-aligned points for click-image events (to be extended with spans later).
 * Props:
 *  - height: number (px)
 */
function ScenarioSpans({ height = 56, runId = null }) {
  const { mouseClickRowsWithImages, currentVideoTime, setSeekTo, selectedScenario, setSelectedScenario, setSpans, spans: currentSpans } = useData();

  const theme = useTheme();
  const secondaryMain = theme?.palette?.secondary?.main || '#9c27b0';
  const secondaryContrast = theme?.palette?.secondary?.contrastText || '#fff';

  // Small color helpers
  const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    const bigint = parseInt(h, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
  };
  const rgbToHex = (r, g, b) => `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  const darkenHex = (hex, factor = 0.2) => {
    try {
      const { r, g, b } = hexToRgb(hex);
      const nr = Math.max(0, Math.min(255, Math.round(r * (1 - factor))));
      const ng = Math.max(0, Math.min(255, Math.round(g * (1 - factor))));
      const nb = Math.max(0, Math.min(255, Math.round(b * (1 - factor))));
      return rgbToHex(nr, ng, nb);
    } catch (e) {
      return hex;
    }
  };

  const [annotations, setAnnotations] = useState([]);

  // Load annotations for the runId (real-time listener)
  useEffect(() => {
    // Clear annotations immediately when runId changes
    setAnnotations([]);
    
    if (!runId) {
      return;
    }
    const annotationsRef = collection(db, 'annotation');
    const q = query(annotationsRef, where('InspectionRunId', '==', runId));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
      setAnnotations(list);
    }, (err) => {
      console.error('Error loading annotations for ScenarioSpans:', err);
      setAnnotations([]);
    });

    return () => unsub();
  }, [runId]);

  // Extract click-image events and map to seconds
  const points = useMemo(() => {
    if (!mouseClickRowsWithImages || !Array.isArray(mouseClickRowsWithImages)) return [];
    const arr = [];
    for (let i = 0; i < mouseClickRowsWithImages.length; ++i) {
      const ev = mouseClickRowsWithImages[i];
      // Use pre-computed clickImageFileName (includes backfilled events)
      if (!ev || !ev.clickImageFileName) continue;
      if (typeof ev.VideoOffset !== 'number') continue;
      const sec = Math.floor(ev.VideoOffset / 1000);
      arr.push({ sec, videoOffsetMs: ev.VideoOffset, event: ev });
    }
    // Remove duplicates for the same second and sort
    arr.sort((a, b) => a.videoOffsetMs - b.videoOffsetMs);
    return arr;
  }, [mouseClickRowsWithImages]);

  // Build scenario spans from annotations
  // Note: This processes all annotations with VideoOffset, including those with special markers
  // (__BEGINNING__ or __ENDING__) or null Image field, as long as they have VideoOffset
  const spans = useMemo(() => {
    if (!annotations || !annotations.length) return [];
    // Filter only intent type annotations (regardless of Image field)
    const intentAnns = annotations.filter(a => a.Type && a.Type.toLowerCase() === 'intent');

    // We'll produce spans per scenarioName => list of { startMs, endMs }
    const namedStacks = {}; // for named Start NAME / End NAME
    const anonStacks = {}; // for simple scenarios using Scenario field
    const result = [];

    // Normalize Note strings and process in chronological order
    const sorted = intentAnns.slice().sort((a, b) => (a.VideoOffset || 0) - (b.VideoOffset || 0));

    for (const ann of sorted) {
      const note = (ann.Note || '').trim();
      const ts = typeof ann.VideoOffset === 'number' ? ann.VideoOffset : 0;
      const actionts = ann.ActionTime ? Date.parse(ann.ActionTime) : null;
      // Parse patterns: 'Start', 'Stop', 'Start NAME', 'End NAME', 'Start/Stop'
      const lower = note.toLowerCase();

      if (lower === 'start/stop' || lower === 'startstop' || lower === 'start/stop') {
        // point in time — render short span centered at ts
        const scenarioName = ann.Scenario || 'unnamed';
        result.push({ scenario: scenarioName, startMs: ts - 50, startActionMs: actionts - 50, endMs: ts + 50, endActionMs: actionts + 50, point: true });
        continue;
      }

      // Check for 'start name' or 'end name'
      const startMatch = note.match(/^start\s+(.+)$/i);
      const endMatch = note.match(/^(end|stop)\s+(.+)$/i);

      if (startMatch) {
        const name = startMatch[1].trim();
        namedStacks[name] = namedStacks[name] || [];
        namedStacks[name].push({ startMs: ts, startActionMs: actionts, ann });
        continue;
      }

      if (endMatch) {
        const name = endMatch[2].trim();
        const stack = namedStacks[name];
        if (stack && stack.length) {
          const open = stack.pop();
          result.push({ scenario: name, startMs: open.startMs, startActionMs: open.startActionMs, endMs: ts, endActionMs: actionts });
        } else {
          // unmatched end — treat as zero-length at ts
          result.push({ scenario: name, startMs: ts, startActionMs: actionts, endMs: ts, endActionMs: actionts });
        }
        continue;
      }

      // Fallback: plain 'start' or 'stop' with optional Scenario field
      if (/^start$/i.test(note)) {
        const name = ann.Scenario || 'unnamed';
        anonStacks[name] = anonStacks[name] || [];
        anonStacks[name].push({ startMs: ts, startActionMs: actionts, ann });
        continue;
      }

      if (/^(stop|end)$/i.test(note)) {
        const name = ann.Scenario || 'unnamed';
        const stack = anonStacks[name];
        if (stack && stack.length) {
          const open = stack.pop();
          result.push({ scenario: name, startMs: open.startMs, startActionMs: open.startActionMs, endMs: ts, endActionMs: actionts });
        } else {
          result.push({ scenario: name, startMs: ts, startActionMs: actionts, endMs: ts, endActionMs: actionts });
        }
        continue;
      }

      // If note contains 'start' or 'stop' not matched above, try simple detection
      if (lower.startsWith('start')) {
        const rest = note.substring(5).trim();
        const name = rest || ann.Scenario || 'unnamed';
        namedStacks[name] = namedStacks[name] || [];
        namedStacks[name].push({ startMs: ts, startActionMs: actionts, ann });
        continue;
      }

      if (lower.startsWith('stop') || lower.startsWith('end')) {
        const rest = note.substring(lower.indexOf(' ') + 1).trim();
        const name = rest || ann.Scenario || 'unnamed';
        const stack = namedStacks[name] || anonStacks[name];
        if (stack && stack.length) {
          const open = stack.pop();
          result.push({ scenario: name, startMs: open.startMs, startActionMs: open.startActionMs, endMs: ts, endActionMs: actionts });
        } else {
          result.push({ scenario: name, startMs: ts, startActionMs: actionts, endMs: ts, endActionMs: actionts });
        }
        continue;
      }
    }

    // Close any remaining open stacks as open-ended spans (endMs = null)
    for (const name in namedStacks) {
      const arr = namedStacks[name];
      for (const open of arr) {
        result.push({ scenario: name, startMs: open.startMs, startActionMs: open.startActionMs, endMs: null, endActionMs: null });
      }
    }
    for (const name in anonStacks) {
      const arr = anonStacks[name];
      for (const open of arr) {
        result.push({ scenario: name, startMs: open.startMs, startActionMs: open.startActionMs, endMs: null, endActionMs: null });
      }
    }

    return result;
  }, [annotations]);

  // Merge spans from annotations with heuristic spans from context for display
  // If manual spans exist, exclude heuristic spans; otherwise include them
  const spansForDisplay = useMemo(() => {
    const manualSpans = spans || [];
    const hasManualSpans = manualSpans.length > 0;
    
    if (hasManualSpans) {
      // Manual spans exist - use only manual spans (heuristic spans will be removed)
      return manualSpans;
    } else {
      // No manual spans - include heuristic spans from context if they exist
      // Filter to only include spans with valid VideoOffset values (startMs and endMs)
      const heuristicSpans = currentSpans 
        ? currentSpans.filter(s => 
            s.isHeuristic && 
            typeof s.startMs === 'number' && 
            typeof s.endMs === 'number' &&
            !isNaN(s.startMs) && 
            !isNaN(s.endMs)
          )
        : [];
      return heuristicSpans;
    }
  }, [spans, currentSpans]);

  // savespans when spans changes
  // If manual spans are being added from annotations, remove any existing heuristic spans
  useEffect(() => {
    if (spans && spans.length > 0) {
      // Manual spans from annotations exist - these replace all spans (including heuristic)
      // The spans from annotations are always manual, so just set them directly
      setSpans(spans);
    } else {
      // No manual spans from annotations - clear any existing manual spans
      // This allows heuristic spans to be calculated for files without manual annotations
      setSpans(prevSpans => {
        // If there are manual spans (non-heuristic), clear them
        const hasManualSpans = prevSpans && prevSpans.some(s => !s.isHeuristic);
        if (hasManualSpans) {
          // Clear manual spans, but preserve heuristic spans if they exist
          const heuristicSpans = prevSpans.filter(s => s.isHeuristic);
          return heuristicSpans;
        }
        // If no manual spans, keep existing spans (might be heuristic or empty)
        return prevSpans;
      });
    }
  }, [spans, setSpans]);

  // Keep same viewBox width approach as Sparkline for alignment
  const w = 400;
  const dotRadius = 4;
  const marginX = dotRadius + 2;

  // Use seconds as floating values (ms -> sec) so placement is accurate
  const pointSecs = points.map(p => p.videoOffsetMs / 1000);
  const spanStartSecs = spansForDisplay.map(s => (typeof s.startMs === 'number' ? s.startMs / 1000 : null)).filter(Boolean);
  const spanEndSecs = spansForDisplay.map(s => (typeof s.endMs === 'number' ? s.endMs / 1000 : null)).filter(Boolean);
  const allSecsArr = pointSecs.concat(spanStartSecs).concat(spanEndSecs);
  let minSec = 0;
  let maxSec = 1;
  if (allSecsArr.length) {
    minSec = Infinity;
    maxSec = -Infinity;
    for (const s of allSecsArr) {
      if (typeof s !== 'number' || !Number.isFinite(s)) continue;
      if (s < minSec) minSec = s;
      if (s > maxSec) maxSec = s;
    }
    if (!Number.isFinite(minSec) || !Number.isFinite(maxSec)) {
      minSec = 0;
      maxSec = 1;
    } else if (maxSec <= minSec) {
      maxSec = minSec + 1;
    }
  }
  const range = Math.max(1e-3, maxSec - minSec);

  const xForSec = (s) => marginX + ((s - minSec) / range) * (w - 2 * marginX);

  // Layout bars into rows to avoid vertical overlap
  const laidOut = useMemo(() => {
    // Create a shallow copy of spans with computed start/end seconds
    const sarr = spansForDisplay.map((s, i) => {
      const start = (typeof s.startMs === 'number') ? s.startMs / 1000 : minSec;
      // If end is null, extend to maxSec (so it appears open)
      const end = (typeof s.endMs === 'number') ? s.endMs / 1000 : Math.max(start + 1, maxSec);
      return { ...s, start, end, __idx: i };
    }).sort((a, b) => a.start - b.start || (a.end - b.end));

    const rows = []; // each row is array of spans
    for (const s of sarr) {
      // find first row where this span doesn't overlap existing last span
      let placed = false;
      for (let ri = 0; ri < rows.length; ++ri) {
        const last = rows[ri][rows[ri].length - 1];
        if (last.end <= s.start) {
          rows[ri].push(s);
          placed = true;
          break;
        }
      }
      if (!placed) {
        rows.push([s]);
      }
    }

    return { rows, count: rows.length, spansByIndex: sarr };
  }, [spansForDisplay, minSec, maxSec]);

  const rowsCount = laidOut.count || 0;

  // Compute SVG height: top area for dots + stacked bars area
  const topArea = Math.max(28, height * 0.5); // allocate more space for dots
  // Keep original bar sizing to match other controls; label padding handled by textY offset
  const barHeight = Math.max(12, Math.round(height * 0.25));
  const barGap = Math.max(6, Math.round(height * 0.12));
  const barsArea = rowsCount > 0 ? rowsCount * (barHeight + barGap) : 0;
  const h = topArea + barsArea + 6;

  // Dot vertical position
  const y = topArea / 2;

  // current second bucket from currentVideoTime
  const currentSec = typeof currentVideoTime === 'number' ? currentVideoTime : null;

  // Early return: if no points and no spans, nothing to render
  if (!points.length && !spansForDisplay.length) return null;

  // Percentage helpers for HTML overlay labels (mirrors xForSec logic including marginX)
  const pctForSec = (s) => (xForSec(s) / w) * 100;

  return (
    <Box sx={{ width: '100%', height: 'auto', overflow: 'visible', userSelect: 'none', m: 0, p: 0, position: 'relative' }}>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }} preserveAspectRatio="none">
        {/* Render scenario bars in stacked rows (behind dots) */}
        {laidOut.rows.map((row, ri) => {
          const rowYTop = topArea + ri * (barHeight + barGap);
          return (
            <g key={`row-${ri}`}>
              {row.map((s) => {
                const x1 = xForSec(s.start);
                const x2 = xForSec(s.end);
                const widthPx = Math.max(2, Math.abs(x2 - x1) || 2);
                const x = Math.min(x1, x2) - (widthPx === 2 ? 1 : 0);
                const colorHash = (s.scenario || 'default').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
                const colors = ['#e57373', '#64b5f6', '#81c784', '#ffb74d', '#9575cd', '#4db6ac'];
                const fill = colors[colorHash % colors.length];
                const isSelected = selectedScenario && (
                  (selectedScenario.startMs === s.startMs && (selectedScenario.endMs === s.endMs || (selectedScenario.endMs == null && s.end == null))) ||
                  (selectedScenario.name && selectedScenario.name === s.scenario && selectedScenario.startMs === s.startMs)
                );

                return (
                  <rect
                    key={`span-${s.__idx}`}
                    x={x}
                    y={rowYTop}
                    width={widthPx}
                    height={barHeight}
                    fill={fill}
                    opacity={isSelected ? 0.72 : 0.36}
                    rx={4}
                    stroke={isSelected ? darkenHex(fill, 0.28) : 'none'}
                    strokeWidth={isSelected ? Math.max(1, Math.round(barHeight * 0.12)) : 0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedScenario(null);
                      } else {
                        setSelectedScenario({ name: s.scenario, startMs: s.startMs, startActionMs: s.startActionMs, endMs: s.endMs, endActionMs: s.endActionMs });
                        if (typeof s.startMs === 'number') setSeekTo(s.startMs / 1000);
                      }
                    }}
                  />
                );
              })}
            </g>
          );
        })}

        {/* Render points/dots on top */}
        {points.map((p, idx) => {
          const cx = xForSec(p.videoOffsetMs / 1000);
          const isCurrent = currentSec !== null && Math.floor(currentSec) === Math.floor(p.videoOffsetMs / 1000);
          return (
            <circle
              key={`${p.videoOffsetMs}-${idx}`}
              cx={cx}
              cy={y}
              r={isCurrent ? dotRadius + 1 : dotRadius}
              fill={isCurrent ? secondaryMain : secondaryContrast}
              stroke={secondaryMain}
              strokeWidth={isCurrent ? 2 : 1}
              style={{ cursor: 'pointer' }}
              onClick={() => setSeekTo(p.videoOffsetMs / 1000)}
            />
          );
        })}
      </svg>
      {/* HTML labels overlaid on top of SVG bars — not stretched */}
      {laidOut.rows.map((row, ri) => {
        const rowYTop = topArea + ri * (barHeight + barGap);
        return row.map((s) => {
          const leftPct = pctForSec(s.start);
          const rightPct = pctForSec(s.end);
          const widthPct = Math.max(0.5, rightPct - leftPct);
          const label = s.scenario || 'unnamed';
          const isSelected = selectedScenario && (
            (selectedScenario.startMs === s.startMs && (selectedScenario.endMs === s.endMs || (selectedScenario.endMs == null && s.end == null))) ||
            (selectedScenario.name && selectedScenario.name === s.scenario && selectedScenario.startMs === s.startMs)
          );
          return (
            <Box
              key={`label-${s.__idx}-${label}`}
              onClick={() => {
                if (isSelected) {
                  setSelectedScenario(null);
                } else {
                  setSelectedScenario({ name: s.scenario, startMs: s.startMs, startActionMs: s.startActionMs, endMs: s.endMs, endActionMs: s.endActionMs });
                  if (typeof s.startMs === 'number') setSeekTo(s.startMs / 1000);
                }
              }}
              sx={{
                position: 'absolute',
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                top: rowYTop,
                height: barHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            >
              <span style={{
                fontSize: 11,
                fontWeight: isSelected ? 600 : 400,
                color: '#000',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1,
              }}>
                {label}
              </span>
            </Box>
          );
        });
      })}
    </Box>
  );
}

export default ScenarioSpans;
